import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { HeartIcon } from "@/components/icons/heart-icon";
import { FilledButton } from "@/components/ui/buttons";
import { useToast } from "@/hooks/ui/use-toast";
import { memo } from "react";
import { useAddFavouriteMeal } from "../../hooks/use-add-favourite-meal";
import { useGetFavouriteMeals } from "../../hooks/use-get-favourite-meals";

interface LikedFoodsOptionProps {
  meal: Meal;
  mealTime: MealTime | undefined;
  onComplete?: () => void;
}

/**
 * Liked Foods option button for Add Meal To modal
 * Handles adding/removing meals from favourite meals list
 */
export const LikedFoodsOption = memo(function LikedFoodsOption({
  meal,
  mealTime,
  onComplete,
}: LikedFoodsOptionProps) {
  const { favouriteMealsSet, isFavouriteLoading } = useGetFavouriteMeals();
  const currentMealId = meal?.id ?? "";
  const isLiked = currentMealId ? favouriteMealsSet.has(currentMealId) : false;
  const { addFavouriteMealAsync, isAddingFavouriteMeal } = useAddFavouriteMeal();
  const { showSuccess, showError } = useToast();

  const handleClick = async () => {
    if (isLiked) {
      onComplete?.();
      return;
    }
    if (!meal?.id || !mealTime) {
      return;
    }

    try {
      if (isAddingFavouriteMeal) {
        return;
      }
      await addFavouriteMealAsync({
        meal_id: meal.id,
        meal_time: mealTime,
      });
      showSuccess("Meal added to liked foods.");

      onComplete?.();
    } catch (error) {
      console.error(error);
      showError("Failed to add/remove meal from liked foods");
    }
  };

  const isLoading = isAddingFavouriteMeal;

  return (
    <FilledButton
      color="black"
      textCase="normal"
      onClick={handleClick}
      disabled={isFavouriteLoading || isLoading}
      className="flex items-center justify-between gap-3 px-5 py-2"
    >
      <HeartIcon className="h-6 w-6 fill-white" />

      <span>Add to liked foods</span>
      <div className="w-6" />
    </FilledButton>
  );
});
